import { HttpErrorResponse } from '@angular/common/http';
import { DatabaseModel, DatabaseRecord } from '../models/database-record.class';
import { DocumentRecord } from '../models/document-interfaces';
import { SyncLoggingService } from '../../logging';
import { concat, intersection } from 'lodash';
import { forkJoin, isObservable, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { OfflineService } from './offline.service';
import { ObserverService } from './observer.service';

export class OfflineFirstDataService {
  protected syncKey: string;
  public syncsTotal: number;
  public syncsDone: number;

  private get syncProgress() {
    return this.syncsDone / (this.syncsTotal || 1);
  }

  public syncProgressSubject = new Subject<number>();
  protected updateTotalProgress(interval: number = 1, skipUpdate: boolean = false) {
    this.syncsDone += interval;
    this.syncProgressSubject.next(this.syncProgress);
  }

  constructor(protected offline: OfflineService,
              protected syncLog: SyncLoggingService,
              protected observerService: ObserverService) {}

  /**
   * Initialize the offline-first service.
   *
   * We attempt to restore data from offline cache,
   * if there is some, we use that and are done.
   *
   * If there is no data available, we will attempt
   * to sync from Internet if we can.
   */
  public init(): Observable<any> {
    return this.restoreData().pipe(
      concatMap(() => this.hasData()),
      concatMap(hasData => {
        if (!hasData && this.offline.isOnline()) {
          return this.push().pipe(
            concatMap(() => this.pull()),
            concatMap(() => this.restoreData()),
          );
        } else {
          return of(null);
        }
      }),
    );
  }

  protected prepareSync(): Observable<any> {
    return of(null);
  }

  /**
   * Force two-way sync with the server
   */
  public sync(): Observable<void> {
    this.syncProgressSubject.next(0);

    this.observerService.notify('Sync started');

    return this.prepareSync().pipe(
      // first restore offline data into service
      concatMap(() => this.restoreData()),
      tap(() => {
        this.updateTotalProgress(1);
      }),
      // then push any updates to the server
      concatMap(() => this.push()),
      // then pull/fetch new data
      concatMap(() => this.pull()),
      // pull updated data from database
      concatMap(() => this.restoreData()),
      tap(() => {
        this.observerService.notify('Sync finished');
        this.updateTotalProgress(1);
      }),
    );
  }

  /**
   * Restore data from offline database into service
   */
  protected restoreData(): Observable<any> {
    return of(null);
  }

  /**
   * Determine if we have all the data we need from offline data store to operate
   */
  protected hasData() {
    // TODO: determine if data is available for offline use,
    // this defaults to always re-fetching data on each load when there is network available
    return of(false);
  }

  /**
   * Push updated data to server
   */
  protected push(): Observable<any> {
    return of(null);
  }

  /**
   * Override in child class to pull/fetch all necessary data
   */
  protected pull(): Observable<any> {
    return of(null);
  }

  /**
   * Gets many records of type T from the offline database or the server if the resource versions are not equal
   * @param options.request REST request to perform
   * @param options.transform delegate to transform results of request
   * @param options.model DatabaseRecord type to get
   * @param options.clear true clears object store before adding response
   * @param options.returnOnlyUpdated - not implemented
   */
  protected getMany<T extends DatabaseRecord>(options: {
    request: Observable<any[]>;
    transform?: (data: any) => Observable<any> | any;
    model: DatabaseModel;
    clear?: boolean;
    returnOnlyUpdated?: boolean;
  }): Observable<any[]> {
    const modelName = options.model.recordName;
    this.syncLog.debug(`${modelName}: Getting all records from the server…`);
    const timeStart = performance.now();

    // get all records from the server if resource versions aren't equal
    return options.request.pipe(
      concatMap(records => {
        if (records) {
          const timeEnd = performance.now();

          this.syncLog.debug(`${modelName}: Got ${records.length} record(s) from the server in ${Math.round(timeEnd - timeStart)} ms.`);

          let ob = of(records);

          // transform records if option was provided
          if (options.transform) {
            records = records.map(options.transform as any);

            if (records.length > 0) {
              if (isObservable(records[0])) {
                ob = forkJoin(records as any[]);
              } else {
                ob = of(records);
              }
            } else {
              ob = of([]);
            }
          }

          return ob.pipe(
            concatMap((transformedRecords: any[]) => {
              // clear the object store before inserting values from the server if option was provided
              if (options.clear) {
                return this.offline.clear(options.model).pipe(
                  concatMap(() => this.offline.save(options.model, transformedRecords)),
                  map(() => transformedRecords),
                );
              } else {
                return this.offline.save(options.model, transformedRecords).pipe(map(() => transformedRecords));
              }
            }),
          );
        } else {
          // if no records were returned, fall back to the offline database
          this.syncLog.debug(`${modelName}: Empty response from the server; returning cached data.`);
          return this.offline.getAll<any>(options.model);
        }
      }),
    );
  }

  /**
   *
   * @param options.model database model to get
   * @param options.uuids uuids to get from the server
   * @param options.request request to get document
   * @param options.diffRequest requst to get document diff
   * @param options.clear optional option to clear object store before saving request response
   */
  protected getManyDocuments<TRecord extends DocumentRecord<TDocument>, TDocument>(options: {
    model: DatabaseModel;
    uuids: string[];
    request: (uuid: string) => Observable<TRecord>;
    diffRequest: (record: TRecord) => Observable<TRecord>;
    clear?: boolean;
  }): Observable<TRecord[]> {
    return this.offline.getAll<TRecord>(options.model).pipe(
      concatMap(records => {
        let request: Observable<TRecord[]>;

        const obs = options.uuids.map(uuid => {
          const record = records.find(rec => rec.uuid === uuid)
          if (record) {
            return options.diffRequest(record)
          } else {
            return options.request(uuid)
          }
        })

        this.syncLog.debug(`${options.model.name}: Getting updates from the server…`);
        request = obs.length > 0 ? forkJoin(obs) : of([] as TRecord[])

        const timeStart = performance.now();
        return request.pipe(
          concatMap(documents => {
            const timeEnd = performance.now();
            this.syncLog.debug(
              `${options.model.name}: Got ${documents.length} record(s) from the server in ${Math.round(timeEnd - timeStart)} ms.`,
            );

            let clearTask: Observable<any> = of(null);
            if (options.clear) {
              clearTask = this.offline.clear(options.model).pipe(
                concatMap(() => this.offline.save(options.model, documents)),
                map(() => documents),
              );
            }

            return clearTask.pipe(concatMap(() => this.offline.save(options.model, documents).pipe(map(() => documents))));
          }),
        );
      }),
    );
  }

  /**
   * Syncs any modified records in an object store with the server
   * @param model Database Record Type to sync
   * @param options.pre optional operation to perform pre-sync
   * @param options.created request to perform if created
   * @param options.updated request to perform if updated
   * @param options.deleted request to perform if deleted
   */
  protected syncDirty<T extends DatabaseRecord>(
    model: DatabaseModel | Observable<T[]>,
    options: {
      pre?: (record: DatabaseRecord) => {};
      created: ObservableCallback<T>;
      updated: ObservableCallback<T>;
      deleted: ObservableCallback<T>;
      model: DatabaseModel;
    },
  ): Observable<T[]> {
    const records = isObservable(model) ? model : this.offline.getAll<T>(model);
    return records.pipe(
      concatMap(allRecords => {
        const updatedRecords = allRecords.filter(record => record && record.isDirty);

        const obs: Observable<any>[] = [];

        if (updatedRecords.length > 0) {
          this.syncLog.debug(`${options.model.recordName}: Syncing ${updatedRecords.length} record(s) to the server…`);
        }

        updatedRecords.forEach(record => {
          if (options.pre) {
            options.pre(record);
          }

          let operation: Observable<any> | undefined;
          if (record.createdAt) {
            operation = options.created(record);
          } else if (record.updatedAt && record.updatedAt.isSame(record.lastUpdate)) {
            operation = options.updated(record);
          } else if (record.deletedAt && record.deletedAt.isSame(record.lastUpdate)) {
            operation = options.deleted(record).pipe(
              catchError(err => {
                if (err instanceof HttpErrorResponse && err.status === 404) {
                  this.syncLog.warn(`${options.model.recordName}: ${record.key} has already been deleted.`);
                  // if we got a 404 error when trying to delete something,
                  // it has already been deleted. ignore the error and continue
                  return of(record);
                } else {
                  return throwError(err);
                }
              }),
            );
          }

          if (operation) {
            obs.push(operation);
          }
        });

        if (obs.length === 0) {
          return of([]);
        } else {
          const timeStart = performance.now();
          return forkJoin(obs).pipe(
            concatMap(results => this.cleanUpRecords(updatedRecords, options.model)),
            tap(() => {
              const timeEnd = performance.now();
              this.syncLog.debug(
                `${options.model.recordName}: Synced ${updatedRecords.length} record(s) with server in ${Math.round(
                  timeEnd - timeStart,
                )} ms.`,
              );
            }),
          );
        }
      }),
    );
  }

  private cleanUpRecords<T extends DatabaseRecord>(records: T[], model: DatabaseModel): Observable<T[]> {
    const obs: Observable<any>[] = [];
    const puts = records.filter(rec => rec.createdAt || rec.updatedAt);
    const deletes = records.filter(rec => rec.deletedAt);

    puts.forEach(rec => rec.markClean());
    deletes.forEach(rec => rec.markClean());

    if (puts.length > 0) {
      this.syncLog.trace(`${model.recordName}: Unmarking ${puts.length} record(s) for syncing in the object store...`);
      obs.push(this.offline.save(model, puts));
    }

    if (deletes.length > 0) {
      this.syncLog.trace(`${model.recordName}: Deleting ${deletes.length} deleted record(s) from the object store...`);
      obs.push(...deletes.map((record: DatabaseRecord) => this.offline.delete(model, record)));
    }

    let cleanUp: Observable<any>;
    if (obs.length > 0) {
      cleanUp = forkJoin(obs).pipe(map(() => undefined));
    } else {
      cleanUp = of(undefined);
    }

    return cleanUp.pipe(map(() => concat(puts, deletes)));
  }

  protected deleteRecord(model: DatabaseModel, record: DatabaseRecord): Observable<void> {
    return this.offline.getRecord(model, record.key).pipe(
      concatMap(restoredRecord => {
        if (restoredRecord.createdAt) {
          return this.offline.delete(model, restoredRecord);
        } else {
          restoredRecord.markDeleted();
          return this.offline.save(model, restoredRecord);
        }
      }),
    );
  }
}

type ObservableCallback<T> = (record: T) => Observable<any>;
